import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, ShoppingBag, Columns, Plus, Wallet, Menu, X, LogOut, Globe, Receipt, CalendarDays } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

const CALENDAR_TYPES = ['rental', 'appointment', 'mua', 'penjahit'];

const NAV_GROUPS = [
  {
    groupId: 'main',
    label: null,
    items: [
      { path: '/karyawan', icon: LayoutDashboard, id: 'Dashboard', en: 'Dashboard', exact: true },
    ],
  },
  {
    groupId: 'pesanan',
    label: { id: 'Pesanan', en: 'Orders' },
    items: [
      { path: '/karyawan/order-create', icon: Plus, id: 'Pesanan Baru', en: 'New Order' },
      { path: '/karyawan/orders', icon: ShoppingBag, id: 'Pesanan Saya', en: 'My Orders' },
      { path: '/karyawan/order-board', icon: Columns, id: 'Kanban Board', en: 'Kanban' },
    ],
  },
  {
    groupId: 'shift',
    label: { id: 'Kelola Shift', en: 'Shift' },
    items: [
      { path: '/karyawan/cash-drawer', icon: Wallet, id: 'Kas Saya', en: 'My Cash' },
      { path: '/karyawan/biaya', icon: Receipt, id: 'Biaya Operasional', en: 'Op. Costs' },
    ],
  },
  {
    groupId: 'calendar',
    label: null,
    calendarOnly: true,
    items: [
      { path: '/karyawan/calendar', icon: CalendarDays, id: 'Kalender', en: 'Calendar' },
    ],
  },
];

export default function StaffLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, store, logout, businessType } = useAuth();
  const { lang, toggleLang } = useLanguage();
  const showCalendar = CALENDAR_TYPES.includes(businessType);

  const handleLogout = async () => {
    await logout();
    toast.success('Berhasil keluar');
    navigate('/login');
  };

  const isActive = (item) => item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path);

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-5 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#0f6c44] rounded-xl flex items-center justify-center">
            <span className="text-white text-sm font-bold">K</span>
          </div>
          <div>
            <p className="font-bold text-slate-800 text-sm">KasirWA</p>
            <p className="text-xs text-emerald-600 font-medium">Mode Kasir</p>
            {store?.is_demo_store && <span data-testid="wa-connection-status" className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />WA Terhubung (Demo Mode)</span>}
          </div>
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-4 overflow-y-auto">
        {NAV_GROUPS.map(group => {
          if (group.calendarOnly && !showCalendar) return null;
          return (
            <div key={group.groupId}>
              {group.label && (
                <p className="px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-400 select-none">
                  {lang === 'id' ? group.label.id : group.label.en}
                </p>
              )}
              <div className="space-y-0.5">
                {group.items.map(item => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                      isActive(item) ? 'bg-[#0f6c44] text-white' : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <item.icon size={16} />
                    <span>{lang === 'id' ? item.id : item.en}</span>
                  </Link>
                ))}
              </div>
            </div>
          );
        })}
      </nav>

      <div className="p-3 border-t border-slate-100 space-y-1">
        <button onClick={toggleLang} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-slate-500 hover:bg-slate-50 rounded-xl">
          <Globe size={14} />
          <span>{lang === 'id' ? '🇬🇧 English' : '🇮🇩 Bahasa'}</span>
        </button>
        <div className="flex items-center gap-2 px-3 py-2">
          <div className="w-7 h-7 bg-emerald-100 rounded-full flex items-center justify-center">
            <span className="text-[#0f6c44] text-xs font-bold">{(user?.name || 'U').charAt(0)}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-slate-800 truncate">{user?.name}</p>
            <p className="text-xs text-slate-400">Kasir</p>
          </div>
        </div>
        <button onClick={handleLogout} data-testid="logout-button"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors">
          <LogOut size={15} />
          <span>{lang === 'id' ? 'Keluar' : 'Logout'}</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f8fafc] flex">
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-slate-100 fixed inset-y-0 left-0 z-30">
        <SidebarContent />
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/20 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <aside className={`fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-100 z-50 transform transition-transform duration-300 lg:hidden ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="absolute top-3 right-3">
          <button onClick={() => setSidebarOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100">
            <X size={18} className="text-slate-500" />
          </button>
        </div>
        <SidebarContent />
      </aside>

      <main className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        <header className="bg-white border-b border-slate-100 px-4 py-3 flex items-center gap-3 lg:hidden sticky top-0 z-20">
          <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-xl hover:bg-slate-100">
            <Menu size={20} className="text-slate-600" />
          </button>
          <span className="font-bold text-slate-800">Mode Kasir</span>
        </header>
        {store?.is_demo_store && <div data-testid="demo-mode-banner" className="mx-4 mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900 md:mx-6"><strong>WA Terhubung (Demo Mode) — Simulasi aktif.</strong> Data direset setiap hari dan pesan WhatsApp hanya dicatat di WA Log, tidak dikirim ke WhatsApp asli.</div>}
        <div className="flex-1 p-4 md:p-6 animate-fade-in">{children}</div>
      </main>
    </div>
  );
}
